// src/components/personal/sections/OrdersSection/components/OrderInfo/OrderInfo.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer } from 'lucide-react';

import { Order, OrderStatus, Payment } from '../../types/types';
import { TEXT_STYLES } from '@/theme';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { displayErrorNotification } from '../../error/error-handlers';
import { printElement } from '@/utils/printUtils';

// Импортируем хук для responsive дизайна
import { useResponsive } from '@/hooks/useResponsive';

import OrderDetailsTab from './OrderDetailsTab';
import OrderItemsTab from './OrderItemsTab';
import OrderPaymentsTab from './OrderPaymentsTab';
import OrderPrintContent from './OrderPrintContent';

// Стили для скрытия скроллбара
const noScrollbarStyles = `
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.no-scrollbar::-webkit-scrollbar { 
  display: none;
}
`;

interface OrderInfoProps {
  order: Order;
  onClose: () => void;
  onStatusUpdate: (orderId: string, status: OrderStatus) => Promise<void>;
  onAddPayment: (orderId: string, paymentData: Partial<Payment>) => Promise<void>;
  disabled: boolean;
}

type TabType = 'details' | 'items' | 'payments';

const OrderInfo: React.FC<OrderInfoProps> = ({ 
  order, 
  onClose, 
  onStatusUpdate,
  onAddPayment, 
  disabled 
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [newPayment, setNewPayment] = useState<Partial<Payment>>({
    method: 'cash',
    status: 'paid',
    amount: '',
    transaction_id: ''
  });
  
  // Используем responsive хук
  const { isMobile } = useResponsive();
  
  const [isPrintReady, setIsPrintReady] = useState(true);
  const printRef = React.useRef<HTMLDivElement>(null);

  // Безопасные значения для полей
  const safeOrder = {
    ...order,
    order_data: order.order_data || [],
    payments: order.payments || [],
    cost_data: order.cost_data || {},
    user_data: order.user_data || {}
  };

  useEffect(() => {
    if (printRef.current) {
      setIsPrintReady(true);
    } else {
      setIsPrintReady(false);
    }
  }, [order]);

  const handlePrint = () => {
    const printContents = printRef.current;
    
    if (printContents) {
      printElement(printContents);
    } else {
      displayErrorNotification({
        code: 'PRINT_ERROR',
        message: 'Не удалось подготовить документ для печати'
      });
    }
  };

  // Обработчик изменения статуса заказа
  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (disabled || isSubmitting) return;
    
    const newStatus = e.target.value as OrderStatus;
    
    setIsSubmitting(true);
    try {
      await onStatusUpdate(order.id, newStatus);
    } catch (error) {
      displayErrorNotification({
        code: 'STATUS_UPDATE_ERROR',
        message: t('errors.status_update_error')
      });
      console.error("Error updating status:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Вкладки с типизацией
  const tabs: { id: TabType; label: string }[] = [
    { id: 'details', label: t('order_details.tabs.general') },
    { id: 'items', label: t('order_details.tabs.items') },
    { id: 'payments', label: t('order_details.tabs.payments') },
  ];

  // Стиль для кнопок вкладок с адаптивными размерами
  const tabButtonClass = (isActive: boolean) => `
    px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-colors duration-200 flex-shrink-0
    ${isMobile ? 'text-sm' : TEXT_STYLES.small}
    ${isActive 
      ? 'bg-primary/10 text-primary' 
      : 'text-text hover:bg-background'
    }
  `;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size={isMobile ? "full" : "3xl"}
      mobileMode={isMobile ? "fullscreen" : "normal"}
      draggable={!isMobile}
      swipeToDismiss={isMobile}
      className={isMobile ? "safe-area-padding" : ""}
    >
      {/* Добавляем стили для безопасной области и скрытия скроллбара */}
      <style>{`
        ${noScrollbarStyles}
        .safe-area-padding {
          padding-top: env(safe-area-inset-top, 20px);
        }
        @media (max-width: 640px) {
          .modal-close-button {
            width: 44px !important;
            height: 44px !important;
            top: env(safe-area-inset-top, 10px);
            right: 10px;
          }
        }
      `}</style>

      {/* Заголовок модального окна */}
      <ModalHeader onClose={onClose} className={isMobile ? "pt-8 sm:pt-4" : ""}>
        {t('order_details.order_number', { id: order.id })}
      </ModalHeader>

      {/* Вкладки */}
      <div className="px-3 py-2 sm:px-6 sm:py-2 border-b border-secondary/20">
        <div className="flex overflow-x-auto no-scrollbar" style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${tabButtonClass(activeTab === tab.id)} mr-2`}
              disabled={disabled}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Тело модального окна */}
      <ModalBody scrollable={true} className="mt-2">
        {/* Скрытый блок для печати */}
        <div className="hidden">
          <OrderPrintContent order={safeOrder} ref={printRef} t={t} i18n={i18n} />
        </div>

        {activeTab === 'details' && (
          <OrderDetailsTab
            order={safeOrder}
            disabled={disabled}
            onStatusChange={handleStatusChange}
            t={t}
            i18n={i18n}
          />
        )}

        {activeTab === 'items' && (
          <OrderItemsTab
            order={safeOrder}
            t={t}
          />
        )}

        {activeTab === 'payments' && (
          <OrderPaymentsTab
            order={safeOrder}
            showAddPayment={showAddPayment}
            newPayment={newPayment}
            disabled={disabled}
            isSubmitting={isSubmitting}
            onToggleAddPayment={() => setShowAddPayment(prev => !prev)}
            onInputChange={(field, e) => {
              const target = e.target as HTMLInputElement | HTMLSelectElement;
              setNewPayment(prev => ({
                ...prev,
                [field]: target.value
              }));
            }}
            onAddPayment={async (e) => {
              e.preventDefault();
              if (disabled || isSubmitting || !newPayment.amount) return;
              
              setIsSubmitting(true);
              try {
                await onAddPayment(order.id, {
                  ...newPayment,
                  order: order.id
                });
                
                setNewPayment({
                  method: 'cash',
                  status: 'paid',
                  amount: '',
                  transaction_id: ''
                });
                setShowAddPayment(false);
              } catch (error) {
                displayErrorNotification({
                  code: 'PAYMENT_ERROR',
                  message: t('errors.payment_add_error')
                });
                console.error("Error adding payment:", error);
              } finally {
                setIsSubmitting(false);
              }
            }}
            t={t}
            i18n={i18n}
          />
        )}
      </ModalBody>

      {/* Футер с кнопками */}
      <ModalFooter mobileFlex="auto">
        <button
          onClick={handlePrint}
          className="inline-flex items-center px-3 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 mr-2"
          disabled={!isPrintReady || disabled}
        >
          <Printer className="w-4 h-4 mr-2" />
          {t('actions.print')}
        </button>
        
        <button
          onClick={onClose}
          className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {t('common:close')}
        </button>
      </ModalFooter>

      {/* Индикатор загрузки */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-xl">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default OrderInfo;